<script setup>
import { ref, watch, onMounted, reactive, computed } from 'vue';
import { useToast } from "primevue/usetoast";
import { useMainStore } from '@/stores/store.js';
import { useRouter } from 'vue-router';
import { callAPI, DRUPAL_API_ROUTE } from "@/service/DrupalService";
import tinycolor from 'tinycolor2';
import QrcodesTemplates from '@/views/Qrcodes.vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useI18n } from 'vue-i18n'
import DomainConfigurator from '@/components/DomainConfigurator.vue';

const { t } = useI18n();

const schema = yup.object({
  destination: yup.string().required().matches(/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\/.+$/, t('shortlink.destination.errors.protocols')).label('Destination'),
  monitoringMail: yup.string().email().label('Email address'),
  domain: yup.string().required(),
  customPath: yup.string().min(4).matches(/^[^#%^*\[\]{};':"\\|,.<>/?]*$/).label('customPath')
});

const { defineField, handleSubmit, resetForm, errors, setFieldError } = useForm({
  validationSchema: schema,
});

const [destination] = defineField('destination');
const [domain] = defineField('domain');
const [customPath] = defineField('customPath');
const [monitoringMail] = defineField('monitoringMail');

const shortlinkForm = ref(null);

const toast = useToast();

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  shortlink: {
    type: String,
    default: ''
  }
});
const emit = defineEmits(['update:visible']);;

const localVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
});

const store = useMainStore();
const router = useRouter();

const getTextColor = (value) => {
  const bgColor = tinycolor(value);
  return bgColor.isLight() ? '#000' : '#fff';
};

const qrcodeTemplate = ref('');

const user = ref(JSON.parse(localStorage.getItem('user')));

const home = ref({
  icon: 'pi pi-home'
});
const items = ref([
  {
    label: t('shortlink.title')
  }
]);

const addDomainDialog = ref(false);
const qrcodesKey = ref(0);
const shortlinkUTMDialog = ref(false);
const shortlinkSettingsDialog = ref(false);
const qrcodesTemplatesList = ref();
const shortlinkSettings = ref(
  JSON.parse(localStorage.getItem('shortlinkSettings')) ?? {
    QRCodeToggle: false,
    expirationToggle: false,
    mobiletargetingToggle: false,
    geotargetingToggle: false,
    metaOGToogle: false,
    monitoringToggle: false,
    linkcloakingToggle: false,
    pixelToggle: false,
    administrationToggle: false,
    qrcodeTemplateUUID: false
  }
)
const settings = ref([
  { name: "QR Code personnalisé", key: "QRCodeToggle", permission: user.value.permissions.create_shortlinks_with_a_custom_qrcode },
  { name: "Expiration", key: "expirationToggle", permission: user.value.permissions.create_shortlinks_with_an_expiration_date },
  { name: "Ciblage mobile", key: "mobiletargetingToggle", permission: user.value.permissions.create_shortlinks_with_mobile_targeting },
  { name: "Ciblage géographique", key: "geotargetingToggle", permission: user.value.permissions.create_shortlinks_with_geo_targeting },
  { name: "Aperçu sur les réseaux sociaux", key: "metaOGToogle", permission: user.value.permissions.create_shortlinks_with_meta_og },
  { name: "Contrôle de disponibilité", key: "monitoringToggle", permission: user.value.permissions.create_shortlinks_with_monitoring },
  { name: "Link Cloaking", key: "linkcloakingToggle", permission: user.value.permissions.create_shortlinks_with_link_cloaking },
  { name: "Pixel", key: "pixelToggle", permission: user.value.permissions.add_pixel_item },
  { name: "Administration", key: "administrationToggle", permission: user.value.permissions.add_tag },
]);

const initialState = {
  uuid: null,
  domainsList: [],
  lccx_allowed: true,
  TagLoading: false,
  tagsList: [],
  destination: '',
  domainsLoading: false,

  customPath: '',
  customPathDisabled: false,
  customPathStatus: t('shortlink.custom_path.default'),
  customPathInvalid: false,

  domain: '00000000-0000-0000-0000-000000000000',
  domainDisabled: false,
  tags: '',
  note: '',

  QRCodeToggle: false,
  QRCode: '',

  shortlinkToggle: true,
  administrationToggle: false,

  expirationToggle: false,
  expirationMinDate: new Date(),
  expirationDate: null,
  expirationURL: '',

  mobiletargetingToggle: false,
  mobiletargetingIOS: '',
  mobiletargetingAndroid: '',
  deeplink: false,

  geotargetingToggle: false,
  countriesList: '',
  countryGeo: null,
  destinationGeo: '',
  countriesGeo: [],
  countriesLoading: false,

  metaOGToogle: false,
  metaOGTitle: '',
  metaOGDescription: '',
  metaOGImage: '',

  monitoringToggle: false,
  monitoringCodes: [
    { 'code': 200, 'status': '200 (Found)' },
    { 'code': 301, 'status': '301 (Moved Permanently)' },
    { 'code': 302, 'status': '302 (Found)' }
  ],
  monitoringCode: '',
  monitoringMail: '',

  pixelToggle: false,
  pixelsList: [],
  pixel: null,
  pixelsListLoading: false,

  linkcloakingToggle: false,
  linkcloaking: false,

  generateLoading: false,
  shortlinkLoading: false,
  customPathInputLoading: false,
  customPathInputLoadingAI: false
};

const formData = reactive({ ...initialState });

const isAllowed = computed(() => user.value.plan.max_shortlinks > user.value.plan.shortlinks_usage);

const fetchDomains = async (refresh = false, uuid = false) => {
  const currentWorkspace = user.value.current_workspace;

  if (refresh) {
    formData.domainsLoading = true;
    const response = await callAPI(DRUPAL_API_ROUTE.WORKSPACES_DOMAINS_LIST, { workspace: currentWorkspace, type: 'shortlinks' });
    formData.domainsList = response.data;
    formData.domainsLoading = false;
  } else {
    formData.domainsList = JSON.parse(localStorage.getItem('shortlinksDomains'));
  }

  const workspaceData = JSON.parse(localStorage.getItem('workspaces'));
  const currentWorkspaceData = workspaceData.find(item => item.id === currentWorkspace);
  if (!currentWorkspaceData.lccx_allowed) {
    formData.lccx_allowed = false;
    formData.domainsList = formData.domainsList.filter(
      d => d.uuid !== formData.domain
    );
  } else {
    domain.value = formData.domain;
  }
  if (currentWorkspaceData.default_domain) domain.value = currentWorkspaceData.default_domain;
  if (uuid) domain.value = uuid;
};

const fetchTags = async () => {
  formData.TagLoading = true;

  var response = await callAPI(DRUPAL_API_ROUTE.TAGS);
  formData.tagsList = response.data;

  formData.TagLoading = false;

};

const fetchCountry = async () => {
  formData.countriesLoading = true;

  var response = await callAPI(DRUPAL_API_ROUTE.MISCELLANEOUS_COUNTRIES, { language: user.value.language });
  formData.countriesList = response.data;

  formData.countriesLoading = false;

};

const addCountry = async () => {
  var add = true;
  if (formData.countriesGeo.length > 0) {
    for (const [index, countryItem] of formData.countriesGeo.entries()) {
      if (countryItem.code == formData.countryGeo.code) {
        formData.countriesGeo[index].destination = formData.destinationGeo;
        add = false;
      }
    }
  }

  if (add) formData.countriesGeo.push({
    'code': formData.countryGeo.code,
    'country': formData.countryGeo.name,
    'destination': formData.destinationGeo
  });

  formData.countryGeo = null;
  formData.destinationGeo = null;
};

const deleteCountry = async (index) => {
  formData.countriesGeo.splice(index, 1);
};

const fetchPixelsList = async () => {
  formData.pixelsListLoading = true;

  var response = await callAPI(DRUPAL_API_ROUTE.PIXELS);
  formData.pixelsList = response.data;

  formData.pixelsListLoading = false;
}

async function customPathInput() {
  if (!user.value.permissions.create_shortlinks_with_a_custom_slug) return;
  formData.customPath = customPath.value;
  formData.domain = domain.value;

  formData.customPathInvalid = false;
  if (formData.customPath.length == 0) {
    resetForm({
      values: {
        destination: destination.value,
      },
    });
    return formData.customPathStatus = '';
  }

  formData.customPathInvalid = false;
  formData.customPathStatus = '';

  if (formData.customPath.length < 4) return;

  formData.customPathStatus = t('shortlink.custom_path.validation');
  formData.customPathInputLoading = true;

  try {
    var response = await callAPI(DRUPAL_API_ROUTE.BULK_TEST, {}, { 'paths': [formData.customPath], 'domain': formData.domain });

    if (response.data.code == 404) {
      if (formData.customPath.match(/[^\u0000-\u007F]/g)) {
        formData.customPathStatus = t('shortlink.custom_path.available_nonascii');
      } else {
        formData.customPathStatus = t('shortlink.custom_path.available');
      }
    }
    if (response.data.code == 400) {
      formData.customPathInvalid = true;
      setFieldError('customPath', t('shortlink.custom_path.errors.invalid'), false);
    }
    if (response.data.code == 409) {
      formData.customPathInvalid = true;
      setFieldError('customPath', t('shortlink.custom_path.errors.duplicate'), false);
    }
  } catch (err) {
    formData.customPathStatus = t('shortlink.custom_path.errors.default');
  } finally {
    formData.customPathInputLoading = false;
  }
}

const customPathInputAI = async () => {
  if (!user.value.permissions.create_shortlinks_with_a_custom_slug && !user.permissions.create_shortlinks_with_a_custom_slug_powered_with_ai) return;
  if (destination.value.length == 0) return;
  formData.customPathInputLoadingAI = true;

  try {
    var response = await callAPI(DRUPAL_API_ROUTE.SHORTLINK_CUSTOM_PATH_AI, {}, { 'destination': destination.value });
    customPath.value = response.data.content;
  } catch (err) {
    console.log(err);
  } finally {
    formData.customPathInputLoadingAI = false;
  }
}

const getQRCodesTemplates = async () => {
  var response = await callAPI(DRUPAL_API_ROUTE.QRCODES_TEMPLATES);
  qrcodesTemplatesList.value = response.data;
};

const generateQRCode = async (shortlink) => {
  var data = {
    'uuid': shortlink.id,
    'template': qrcodeTemplate.value.QRCodesTemplateSelected?.id || false,
    'shortlink': shortlink.shorturl || 'https://lc.cx',
    'action': 'generateOneTimeCustomQRCode',
    'preview': false,
    'title': qrcodeTemplate.value.QRCodeFrameTitle || false,
    'color': formatQRCodeHexString(qrcodeTemplate.value.QRCodeColor) || false,
    'logo': qrcodeTemplate.value.QRCodeLogo || false,
    'shape': qrcodeTemplate.value.QRCodeShape || false,
    'eyes': qrcodeTemplate.value.QRCodeEyes || false,
    'po_tl': formatQRCodeHexString(qrcodeTemplate.value.QRCodePOTL) || false,
    'pi_tl': formatQRCodeHexString(qrcodeTemplate.value.QRCodePITL) || false,
    'po_tr': formatQRCodeHexString(qrcodeTemplate.value.QRCodePOTR) || false,
    'pi_tr': formatQRCodeHexString(qrcodeTemplate.value.QRCodePITR) || false,
    'po_bl': formatQRCodeHexString(qrcodeTemplate.value.QRCodePOBL) || false,
    'pi_bl': formatQRCodeHexString(qrcodeTemplate.value.QRCodePIBL) || false,
    'frame': qrcodeTemplate.value.QRCodeFrame || false,
    'frame_color': formatQRCodeHexString(qrcodeTemplate.value.QRCodeFrameColor) || false,
    'frame_text_color': qrcodeTemplate.value.QRCodeFrameTextColor || false,
  }

  if (data.template) data.action = "generateQRCodeFromTemplate";
  var response = await callAPI(DRUPAL_API_ROUTE.SHORTLINK_QRCODE_CREATE, {}, data);

  var qrcode = response.data;
  qrcodeTemplate.value = '';

  return qrcode;
}

const onSubmit = handleSubmit((values) => {
  if (formData.customPathInvalid) return;
  formData.destination = values.destination;
  formData.domain = values.domain;
  formData.monitoringMail = values.monitoringMail;
  formData.customPath = values.customPath;

  var update = (formData.uuid) ? true : false;
  generateShortlink(update);
});

const generateShortlink = async (update = false) => {
  if (formData.generateLoading) return;

  formData.generateLoading = true;

  var data = {
    "destination": formData.destination,
    "domain": formData.domain
  };

  if (formData.customPath) data.custom_path = formData.customPath;
  data.note = formData.note ?? '';
  if (formData.tags) data.tags = formData.tags;

  var expiration = false;

  if (formData.expirationDate || !formData.gdpr12month) {
    if (user.value.permissions.create_shortlinks_with_12_month_expiration) var date = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString();
    if (formData.expirationDate) var date = new Date(formData.expirationDate).toISOString();

    if (date) {
      expiration = {};

      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');

      expiration.date = `${year}-${month}-${day}`;
      expiration.url = formData.expirationURL || false;
    }
  }

  var mobileTargeting = {};
  mobileTargeting.os = {};
  if (formData.mobiletargetingIOS) mobileTargeting.os.ios = formData.mobiletargetingIOS;
  if (formData.mobiletargetingAndroid) mobileTargeting.os.android = formData.mobiletargetingAndroid;
  if (formData.deeplink) mobileTargeting.os.deeplink = true;
  if (Object.keys(mobileTargeting.os).length == 0) mobileTargeting = false;

  var geoTargeting = [];
  for (const itemGeo of formData.countriesGeo) {
    geoTargeting.push({ 'country': itemGeo.code, 'destination': itemGeo.destination })
  }
  if (Object.keys(geoTargeting).length == 0) geoTargeting = false;

  var metaOG = {};
  if (formData.metaOGTitle) metaOG.og_title = formData.metaOGTitle;
  if (formData.metaOGDescription) metaOG.og_description = formData.metaOGDescription;
  if (formData.metaOGImage) metaOG.og_image = formData.metaOGImage;
  if (Object.keys(metaOG).length == 0) metaOG = false;

  if (formData.monitoringCode && formData.monitoringMail) {
    data.monitoring = {};
    data.monitoring.code = formData.monitoringCode;
    data.monitoring.mail = formData.monitoringMail;
    data.monitoring.locale = 'fr';
  } else {
    data.monitoring = false;
  }

  var extras = {};
  extras.pixel = formData.pixel || false;
  extras.expiration = expiration;
  extras.hidereferrer = false;
  extras.linkcloaking = formData.linkcloaking;
  extras.geo = geoTargeting;
  extras.mobile = mobileTargeting;
  extras.meta_og = metaOG;

  data.extras = extras;

  data.journal = user.value.permissions.save_log;

  if (!update) {
    try {
      var response = await callAPI(DRUPAL_API_ROUTE.SHORTEN, {}, data);
      var shortlink = response.data;
    } catch (err) {
      console.log(err);
    } finally {
      generateQRCode(shortlink);
      formData.generateLoading = false;
      localVisible.value = false;
      store.setMessage(shortlink);
      resetFormData();
      resetForm();
      router.push({
        path: '/shortlinks',
        force: true
      });
    }
  } else {
    try {
      var response = await callAPI(DRUPAL_API_ROUTE.SHORTLINK_UPDATE, { uuid: formData.uuid }, data);
      var shortlink = response.data;
    } catch (err) {
      console.log(err);
    } finally {
      formData.generateLoading = false;
      localVisible.value = false;
      shortlink.shorturl = shortlink.shortlink;
      store.setMessage(shortlink);
      resetFormData();
      resetForm();
      router.push({
        path: '/shortlinks',
        force: true
      });
    }
  }

};

async function fetchShortlink(uuid) {
  formData.shortlinkLoading = true;

  var response = await callAPI(DRUPAL_API_ROUTE.SHORTLINK, { 'uuid': uuid });
  var shortlink = response.data;


  formData.uuid = shortlink.id;
  formData.destination = shortlink.destination;
  destination.value = shortlink.destination;
  formData.domain = shortlink.domain.id;
  formData.shortlink = shortlink.shortlink;
  formData.domainDisabled = true;
  formData.customPath = shortlink.path;
  formData.customPathDisabled = true;

  formData.expirationDate = shortlink.extras.expiration?.date;
  formData.expirationURL = shortlink.extras.expiration?.url || '';
  if (formData.expirationDate) formData.expirationToggle = true;

  formData.mobiletargetingIOS = shortlink.extras.mobile?.os?.ios;
  formData.mobiletargetingAndroid = shortlink.extras.mobile?.os?.android;
  formData.deeplink = shortlink.extras.mobile?.os?.deeplink;
  if (formData.mobiletargetingIOS || formData.mobiletargetingAndroid || formData.deeplink) formData.mobiletargetingToggle = true;

  if (shortlink.extras.geo.length > 0) {
    formData.geotargetingToggle = true;
    for (const geo of shortlink.extras.geo) {
      var country = formData.countriesList.find((element) => element.code == geo.country);
      formData.countriesGeo.push({ 'code': geo.country, 'country': country.name, 'destination': geo.destination })
    }
  }
  formData.metaOGTitle = shortlink.extras.meta_og?.og_title;
  formData.metaOGDescription = shortlink.extras.meta_og?.og_description;
  formData.metaOGImage = shortlink.extras.meta_og?.og_image;
  if (formData.metaOGTitle || formData.metaOGDescription || formData.metaOGImage) formData.metaOGToogle = true;


  formData.monitoringCode = shortlink.monitoring.code;
  formData.monitoringMail = shortlink.monitoring.mail;
  if (formData.monitoringCode && formData.monitoringMail) formData.monitoringToggle = true;

  formData.linkcloaking = shortlink.extras.linkcloaking;
  if (formData.linkcloaking) formData.linkcloakingToggle = true;

  formData.pixel = shortlink.extras.pixel;
  if (formData.pixel) formData.pixelToggle = true;

  if (shortlink.tags) {
    formData.tags = [];
    for (const tag of shortlink.tags) {
      formData.tags.push(tag.id);
    }
  }
  formData.note = shortlink.note;
  if (formData.note || formData.tags) formData.administrationToggle = true;

  formData.screenshot = shortlink.screenshot;

  formData.customPathStatus = '';
}

const shortlinkUTMProcess = () => {
  var baseUrl = destination.value;

  var params = {
    'source': formData.UTMSource,
    'medium': formData.UTMMedium,
    'campaign': formData.UTMCampaign,
    'term': formData.UTMTerm,
    'content': formData.UTMContent
  }

  var url = new URL(baseUrl);
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  destination.value = url.toString();

  shortlinkUTMDialog.value = false;
}

const shortlinkSettingsShow = async () => {
  await getQRCodesTemplates();
}

const shortlinkSettingsrocess = async () => {
  if (!shortlinkSettings.value.QRCodeToggle) shortlinkSettings.value.qrcodeTemplateUUID = false;
  localStorage.setItem('shortlinkSettings', JSON.stringify(shortlinkSettings.value));
  Object.keys(shortlinkSettings.value).forEach(key => {
    if (key in formData) formData[key] = shortlinkSettings.value[key];
  });
  if (formData.administrationToggle) await fetchTags();
  if (formData.geotargetingToggle) await fetchCountry();
  if (formData.pixelToggle) await fetchPixelsList();
  qrcodesKey.value++;
  shortlinkSettingsDialog.value = false;
}

const domainDeeplink = () => {
  const domainUUID = domain.value;
  const selectedDomain = formData.domainsList.find(item => item.uuid === domainUUID);

  if (selectedDomain?.deeplinks) {
    return Object.keys(selectedDomain.deeplinks).length === 0 ? true : false;
  }

  return true;
}

function resetFormData() {
  Object.assign(formData, initialState);
}

async function handleShow() {
  if (props.shortlink) {
    formData.shortlinkLoading = true;
  } else {
    Object.keys(shortlinkSettings.value).forEach(key => {
      var setting = settings.value.find(setting => setting.key === key);
      if (setting) {
        if (setting.permission) formData[key] = shortlinkSettings.value[key]
      }
    });
  }

  fetchDomains();
  if (props.shortlink) {
    fetchTags();
    fetchCountry();
    fetchPixelsList();
    await fetchShortlink(props.shortlink);
    formData.shortlinkLoading = false;
  }
}

async function handleAfterShow() {
  if (!props.shortlink) {
    if (formData.administrationToggle) await fetchTags();
    if (formData.geotargetingToggle) await fetchCountry();
    if (formData.pixelToggle) await fetchPixelsList();
  }
}

function handleHide() {
  resetForm();
  resetFormData();
  formData.countriesGeo = [];
  emit('update:visible', false);
}

function formatQRCodeHexString(input) {
  return (input) ? input.startsWith('#') ? input : `#${input}` : false;
}

</script>


<template>
  <DomainConfigurator v-model:visible="addDomainDialog" @update:visible="(val) => { if (!val) fetchDomains() }"
    @domain="(domain) => { if (domain?.id && domain?.status == 1) fetchDomains(true, domain.id) }" />

  <Dialog v-model:visible="shortlinkUTMDialog" modal :header="t('shortlink.utm.title')" :style="{ width: '25rem' }">
    <div class="flex flex-col gap-2 mb-4">
      <label for="name" class="font-semibold">Source</label>
      <InputText id="name" class="flex-auto" v-model="formData.UTMSource" autocomplete="off" />
    </div>
    <div class="flex flex-col gap-2 mb-4">
      <label for="name" class="font-semibold">Medium</label>
      <InputText id="name" class="flex-auto" v-model="formData.UTMMedium" autocomplete="off" />
    </div>
    <div class="flex flex-col gap-2 mb-4">
      <label for="name" class="font-semibold">Campaign</label>
      <InputText id="name" class="flex-auto" v-model="formData.UTMCampaign" autocomplete="off" />
    </div>
    <div class="flex flex-col gap-2 mb-4">
      <label for="name" class="font-semibold">Term</label>
      <InputText id="name" class="flex-auto" v-model="formData.UTMTerm" autocomplete="off" />
    </div>
    <div class="flex flex-col gap-2 mb-8">
      <label for="name" class="font-semibold">Content</label>
      <InputText id="name" class="flex-auto" v-model="formData.UTMContent" autocomplete="off" />
    </div>
    <div class="flex justify-end gap-2">
      <Button type="button" :label="t('common.labels.cancel')" severity="secondary"
        @click="shortlinkUTMDialog = false"></Button>
      <Button type="button" :label="t('common.labels.add')" @click="shortlinkUTMProcess"></Button>
    </div>
  </Dialog>

  <Dialog v-model:visible="shortlinkSettingsDialog" @show="shortlinkSettingsShow" modal
    :header="t('shortlink.settings.title')" :style="{ width: '25rem' }">
    <div class="flex flex-col gap-4 mb-8">
      <label for="settings" class="font-semibold">{{ t('shortlink.settings.content') }}</label>
      <div v-for="setting of settings" :key="setting.key" class="flex items-center gap-2">
        <div class="flex flex-col space-y-2">
          <div class="flex flex-inline gap-2">
            <ToggleSwitch v-model="shortlinkSettings[setting.key]" :inputId="setting.key" name="settings"
              :value="setting.key" :disabled="!setting.permission" />
            <label :for="setting.key">{{ setting.name }}</label>
          </div>
          <div v-if="setting.key == 'QRCodeToggle' && shortlinkSettings[setting.key]" class="flex flex-col gap-2 mb-4">
            <label for="qrcodes">{{ t('qrcodes.templates') }}</label>
            <Select v-model="shortlinkSettings[('qrcodeTemplateUUID')]" :options="qrcodesTemplatesList"
              :placeholder="t('common.placeholders.select')" optionLabel="name" showClear />
          </div>
        </div>
      </div>
    </div>
    <div class="flex justify-end gap-2">
      <Button type="button" :label="t('common.labels.cancel')" severity="secondary"
        @click="shortlinkSettingsDialog = false"></Button>
      <Button type="button" :label="t('common.labels.save')" @click="shortlinkSettingsrocess"></Button>
    </div>
  </Dialog>

  <Fluid>
    <Drawer :blockScroll="true" :dismissable="false" @hide="handleHide" @show="handleShow" @after-show="handleAfterShow"
      v-model:visible="localVisible" class="!w-full md:!w-1/2 lg:!w-[35rem]">
      <template #header>
        <div class="flex items-center gap-2">
          <Breadcrumb :home="home" :model="items" class="!p-0">
            <template #item="{ item }">
              <span v-if="item.label" class="text-primary mr-2">{{ item.label }}</span>
              <span :class="item.icon"></span>
            </template>
            <template #separator><i class="pi pi-angle-right"></i></template>
          </Breadcrumb>
        </div>
        <div v-if="!props.shortlink" class="ml-auto mr-4"> <Button @click="shortlinkSettingsDialog = true"
            icon="pi pi-cog" severity="secondary" size="small" v-tooltip.bottom="t('common.labels.configure')" /></div>
      </template>
      <div class="flex mx-auto">
        <div class="flex flex-col w-full">
          <form ref="shortlinkForm" @submit="onSubmit" class="space-y-10">
            <div class="flex flex-col gap-2 space-y-4">
              <div class="flex flex-col mt-1" v-if="!user.permissions.update_shortlink && Boolean(formData.uuid)">
                <Message icon="pi pi-info-circle" severity="info">{{ t('shortlink.update.premium') }}
                </Message>
              </div>
              <div class="flex items-center space-x-2">
                <ToggleSwitch v-model="formData.shortlinkToggle"
                  :disabled="!user.permissions.update_shortlink && Boolean(formData.uuid)" />
                <span class="text-xl">{{ t('shortlink.title') }}</span>
                <i class="pi pi-link" />
                <div class="flex-grow"></div>
                <Image v-if="formData.screenshot && !formData.shortlinkLoading" alt="Image" preview>
                  <template #previewicon>
                    <i class="pi pi-search"></i>
                  </template>
                  <template #image>
                    <img class="max-w-24" :src="formData.screenshot" />
                  </template>
                  <template #original="slotProps">
                    <img :src="formData.screenshot" alt="preview" :style="slotProps.style" @click="slotProps.onClick" />
                  </template>
                </Image>
              </div>

              <div v-if="formData.shortlinkToggle" class="space-y-6">
                <div class="flex flex-col gap-2">
                  <label for="destination">{{ t('shortlink.destination.title') }}</label>
                  <Skeleton v-if="formData.shortlinkLoading" height="3rem" class="mb-2">
                  </Skeleton>
                  <InputGroup v-else>
                    <InputText id="destination" size="large" :class="{ 'p-invalid': errors.destination }"
                      :disabled="!user.permissions.update_shortlink && Boolean(formData.uuid)" v-model="destination"
                      placeholder="https://" />
                    <Button icon="pi pi-map-marker" @click="shortlinkUTMDialog = true"
                      :disabled="formData.customPathInputLoading || !user.permissions.create_shortlinks_with_UTM_builder || !destination"
                      v-tooltip.bottom="t('shortlink.utm.title')" />
                  </InputGroup>
                  <Message v-if="errors.destination" severity="error" size="small" variant="simple">{{
                    errors.destination }}
                  </Message>
                </div>
                <div class="flex flex-col gap-2">
                  <div v-if="!props.shortlink">
                    <div class="flex flex-row flex-nowrap">
                      <div class="flex flex-col gap-2">
                        <label for="domain">{{ t('shortlink.domain') }}</label>
                        <InputGroup>
                          <InputGroupAddon>
                            <Button icon="pi pi-refresh" v-tooltip.bottom="t('common.labels.refresh')"
                              severity="secondary" @click="fetchDomains(true)" />
                          </InputGroupAddon>
                          <Select v-model="domain" :options="formData.domainsList" optionValue="uuid"
                            optionLabel="domain" :placeholder="t('common.placeholders.select')"
                            :disabled="formData.domainDisabled" :loading="formData.domainsLoading"
                            :class="{ 'p-invalid': errors.domain }" />
                        </InputGroup>
                        <Message v-if="errors.domain" severity="error" size="small" variant="simple">{{
                          errors.domain }}
                        </Message>
                      </div>
                      <div class="basis-1/12 flex-col gap-2 text-center">
                        <span class="text-4xl mt-8 hidden sm:block">/</span>
                      </div>
                      <div class="grow flex flex-col gap-2">
                        <label for="custom_path">{{ t('shortlink.custom_path.title') }}</label>
                        <Skeleton v-if="formData.shortlinkLoading" height="3rem" class="mb-2">
                        </Skeleton>
                        <IconField v-else>
                          <InputGroup>
                            <InputText v-model="customPath" @input="customPathInput"
                              :disabled="formData.customPathDisabled || !user.permissions.create_shortlinks_with_a_custom_slug"
                              :invalid="formData.customPathInvalid" :class="{ 'p-invalid': errors.customPath }" />
                            <Button icon="pi pi-microchip-ai" @click="customPathInputAI"
                              :loading="formData.customPathInputLoadingAI"
                              :disabled="!destination || formData.customPathInputLoading || formData.customPathDisabled || !user.permissions.create_shortlinks_with_a_custom_slug_powered_with_ai"
                              v-tooltip.bottom="'Suggestion IA'" />
                          </InputGroup>
                        </IconField>
                        <Message v-if="errors.customPath" severity="error" size="small" variant="simple">{{
                          errors.customPath }} </Message>
                        <Message v-if="formData.customPathStatus == t('shortlink.custom_path.available')"
                          severity="success" size="small" variant="simple"><i class="pi pi-check"></i> {{
                            formData.customPathStatus }}
                        </Message>
                        <Message v-if="formData.customPathStatus == t('shortlink.custom_path.available_nonascii')"
                          severity="warn" size="small" variant="simple"><i class="pi pi-exclamation-triangle"></i> {{
                            formData.customPathStatus }}
                        </Message>
                      </div>
                    </div>
                    <div v-if="formData.domainsList.length == 1 && formData.lccx_allowed" class="flex flex-col mt-5">
                      <Fieldset :legend="t('shortlink.custom_domains.title')">
                        <div class="flex items-center justify-between w-full">
                          <div class="w-3/4 pr-2">
                            <p class="mb-0 text-sm">{{ t('shortlink.custom_domains.content') }}</p>
                          </div>
                          <div class="w-1/2 flex justify-end pl-2">
                            <Button :label="t('dashboard.featured.custom_domains.label')"
                              @click="addDomainDialog = true" size="small" />
                          </div>
                        </div>
                      </Fieldset>
                    </div>
                  </div>
                  <div v-else>
                    <div class="flex flex-col gap-2">
                      <label for="domain">{{ t('shortlink.link') }}</label>
                      <Skeleton v-if="formData.shortlinkLoading" height="3rem" class="mb-2">
                      </Skeleton>
                      <InputText v-else v-model="formData.shortlink" :disabled="true" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="!formData.uuid" class="flex flex-col gap-2 space-y-4">
              <div class="flex items-center space-x-2">
                <ToggleSwitch v-model="formData.QRCodeToggle"
                  :disabled="formData.shortlinkLoading || !user.permissions.create_shortlinks_with_a_custom_qrcode" />
                <span class="text-xl">{{ t('shortlink.qrcode') }}</span><i class="pi pi-qrcode me-auto"></i>
              </div>
              <div v-if="formData.QRCodeToggle">
                <QrcodesTemplates :key="qrcodesKey" v-model="qrcodeTemplate"
                  :QRCodesTemplateSelected="shortlinkSettings['qrcodeTemplateUUID']" />
              </div>
            </div>

            <div class="flex flex-col gap-2 space-y-4">
              <div class="flex items-center space-x-2">
                <ToggleSwitch v-model="formData.expirationToggle"
                  :disabled="formData.shortlinkLoading || !user.permissions.create_shortlinks_with_an_expiration_date" />
                <span class="text-xl">{{ t('shortlink.expiration.title') }}</span><i class="pi pi-calendar me-auto"></i>
              </div>
              <div v-if="formData.expirationToggle">
                <div class="flex flex-row space-x-4">
                  <div class="flex flex-col gap-2">
                    <label for="tags">{{ t('shortlink.expiration.date') }}</label>
                    <DatePicker :minDate="formData.expirationMinDate" dateFormat="yy-mm-dd"
                      v-model="formData.expirationDate" showIcon fluid iconDisplay="input" showButtonBar />
                  </div>
                  <div class="flex flex-col gap-2 w-3/4">
                    <label for="note">{{ t('shortlink.expiration.destination') }}</label>
                    <InputText v-model="formData.expirationURL" placeholder="https://" />
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-col gap-2 space-y-4">
              <div class="flex items-center space-x-2">
                <ToggleSwitch v-model="formData.mobiletargetingToggle"
                  :disabled="formData.shortlinkLoading || !user.permissions.create_shortlinks_with_mobile_targeting" />
                <span class="text-xl">{{ t('shortlink.mobile_targeting.title') }}</span><i
                  class="pi pi-mobile me-auto"></i>
              </div>
              <div v-if="formData.mobiletargetingToggle" class="flex flex-col gap-8">
                <div class="flex flex-row space-x-4">
                  <div class="flex flex-col gap-2 w-full">
                    <label for="ios"><i class="pi pi-apple me-2"></i>iOS</label>
                    <InputText placeholder="https://" v-model="formData.mobiletargetingIOS" />
                  </div>
                  <div class="flex flex-col gap-2 w-full">
                    <label for="android"><i class="pi pi-android me-2"></i>Android</label>
                    <InputText placeholder="https://" v-model="formData.mobiletargetingAndroid" />
                  </div>
                </div>
                <div class="flex flex-col gap-2">
                  <div class="flex flex-row space-x-2">
                    <Checkbox v-model="formData.deeplink" binary
                      :disabled="domainDeeplink() || !user.permissions.create_shortlinks_with_deeplinks" />
                    <label for="deeplink"> {{ t('shortlink.mobile_targeting.deeplinks.enable') }}</label>
                  </div>
                  <Message v-if="!user.permissions.create_shortlinks_with_deeplinks" size="small" severity="info">
                    {{ t('shortlink.premium') }}
                  </Message>
                  <Message v-if="domainDeeplink()" size="small" severity="info">
                    {{ t('shortlink.mobile_targeting.deeplinks.configuration') }}
                  </Message>
                  <Message v-if="formData.deeplink" size="small" severity="secondary" variant="simple">
                    {{ t('shortlink.mobile_targeting.deeplinks.notice') }}
                  </Message>
                </div>
              </div>
            </div>

            <div class="flex flex-col gap-2 space-y-4">
              <div class="flex items-center space-x-2">
                <ToggleSwitch @update:modelValue="val => val && fetchCountry()" v-model="formData.geotargetingToggle"
                  :disabled="formData.shortlinkLoading || !user.permissions.create_shortlinks_with_geo_targeting" />
                <span class="text-xl">{{ t('shortlink.geo_targeting.title') }}</span><i class="pi pi-globe me-auto"></i>
              </div>
              <div v-if="formData.geotargetingToggle">
                <div class="flex flex-row space-x-10">
                  <div class="flex flex-col gap-2 w-1/2">
                    <label for="country">{{ t('shortlink.geo_targeting.country') }}</label>
                    <Select v-model="formData.countryGeo" :options="formData.countriesList" optionLabel="name" showClear
                      :loading="formData.countriesLoading" />
                  </div>
                  <div class="flex flex-col gap-2 w-1/2">
                    <label for="destination">{{ t('shortlink.geo_targeting.destination') }}</label>
                    <InputText v-model="formData.destinationGeo" placeholder="https://" />
                  </div>
                  <div class="flex gap-2 mt-auto justify-end">
                    <Button @click="addCountry(formData.countryGeo, formData.destinationGeo)" icon="pi pi-plus-circle"
                      v-tooltip.bottom="'Ajouter'" severity="primary" />
                  </div>
                </div>

                <div v-for="(countryGeoItem, index) in formData.countriesGeo" :key="index"
                  class="flex flex-row space-x-10 mt-10">
                  <div class="flex flex-col gap-2 w-1/2">
                    <InputText v-model="countryGeoItem.country" disabled />
                  </div>
                  <div class="flex flex-col gap-2 w-1/2">
                    <InputText v-model="countryGeoItem.destination" disabled />
                  </div>
                  <div class="flex gap-2 mt-auto justify-end">
                    <Button @click="deleteCountry(index)" v-tooltip.bottom="t('common.labels.delete')" severity="danger"
                      icon="pi pi-trash" size="small" />
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-col gap-2 space-y-4">
              <div class="flex items-center space-x-2">
                <ToggleSwitch v-model="formData.metaOGToogle"
                  :disabled="formData.shortlinkLoading || !user.permissions.create_shortlinks_with_meta_og" />
                <span class="text-xl">{{ t('shortlink.og.head') }}</span><i class="pi pi-eye me-auto"></i>
              </div>
              <div v-if="formData.metaOGToogle">
                <div class="flex flex-row space-x-10">
                  <div class="flex flex-col gap-2 w-full">
                    <label for="metaOGTitle">{{ t('shortlink.og.title') }}</label>
                    <InputText v-model="formData.metaOGTitle" />
                  </div>
                  <div class="flex flex-col gap-2 w-full">
                    <label for="metaOGDescription">{{ t('shortlink.og.description') }}</label>
                    <InputText v-model="formData.metaOGDescription" />
                  </div>
                  <div class="flex flex-col gap-2 w-full">
                    <label for="metaOGImage">{{ t('shortlink.og.image') }}</label>
                    <InputText v-model="formData.metaOGImage" />
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-col gap-2 space-y-4">
              <div class="flex items-center space-x-2">
                <ToggleSwitch v-model="formData.monitoringToggle"
                  :disabled="formData.shortlinkLoading || !user.permissions.create_shortlinks_with_monitoring" />
                <span class="text-xl">{{ t('shortlink.monitoring.title') }}</span><i class="pi pi-wifi me-auto"></i>
              </div>
              <div v-if="formData.monitoringToggle">
                <div class="flex flex-row space-x-10">
                  <div class="flex flex-col gap-2 w-full">
                    <label for="monitoringCode"><i class="pi pi-server me-2"></i>{{ t('shortlink.monitoring.code')
                    }}</label>
                    <Select v-model="formData.monitoringCode" :options="formData.monitoringCodes" optionValue="code"
                      optionLabel="status" showClear />
                  </div>
                  <div class="flex flex-col gap-2 w-full">
                    <label for="MonitoringMail"><i class="pi pi-envelope me-2"></i>{{ t('shortlink.monitoring.mail')
                    }}</label>
                    <InputText v-model="monitoringMail" name="email" type="text" placeholder="Email" />
                    <Message v-if="errors.monitoringMail" severity="error" size="small" variant="simple">{{
                      errors.monitoringMail }}
                    </Message>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-col gap-2 space-y-4">
              <div class="flex items-center space-x-2">
                <ToggleSwitch v-model="formData.linkcloakingToggle"
                  :disabled="formData.shortlinkLoading || !user.permissions.create_shortlinks_with_link_cloaking" />
                <span class="text-xl">{{ t('shortlink.linkcloaking.head') }}</span><i
                  class="pi pi-eye-slash me-auto"></i>
              </div>
              <div v-if="formData.linkcloakingToggle">
                <div class="flex flex-row space-x-2">
                  <Checkbox v-model="formData.linkcloaking" binary />
                  <label for="linkcloaking">{{ t('shortlink.linkcloaking.title') }}</label>
                </div>
                <div class="flex flex-col gap-2 mt-5 w-full">
                  <Message size="small" icon="pi pi-info-circle" severity="info">{{ t('shortlink.linkcloaking.notice')
                  }}
                  </Message>
                </div>
              </div>
            </div>

            <div class="flex flex-col gap-2 space-y-4">
              <div class="flex items-center space-x-2">
                <ToggleSwitch @update:modelValue="val => val && fetchPixelsList()" v-model="formData.pixelToggle"
                  :disabled="formData.shortlinkLoading || !user.permissions.add_pixel_item" />
                <span class="text-xl">{{ t('shortlink.pixels.head') }}</span><i class="pi pi-bullseye me-auto"></i>
              </div>
              <div v-if="formData.pixelToggle">
                <div class="flex flex-row space-x-10">
                  <div class="flex flex-col gap-2 w-1/4">
                    <label for="pixels">{{ t('shortlink.pixels.title') }}</label>
                    <Select v-model="formData.pixel" :options="formData.pixelsList" optionValue="uuid"
                      optionLabel="name" showClear :loading="formData.pixelsListLoading">
                      <template #option="slotProps">
                        <div class="flex items-center">
                          <div>{{ slotProps.option.name }}</div>
                        </div>
                      </template>
                    </Select>
                  </div>
                </div>
              </div>
            </div>


            <div class="flex flex-col space-y-6">
              <div class="flex items-center space-x-2">
                <ToggleSwitch @update:modelValue="val => val && fetchTags()" v-model="formData.administrationToggle"
                  :disabled="formData.shortlinkLoading || !user.permissions.add_tag" />
                <span class="text-xl">{{ t('shortlink.administration.title') }}</span><i class="pi pi-tag me-auto"></i>
              </div>
              <div v-if="formData.administrationToggle">
                <div class="flex flex-row space-x-10">
                  <div class="flex flex-col gap-2 w-1/3">
                    <label for="tags">{{ t('shortlink.administration.tags') }}</label>
                    <MultiSelect v-model="formData.tags" :options="formData.tagsList" :loading="formData.TagLoading"
                      :disabled="!user.permissions.add_tag" optionValue="id" optionLabel="name" filter display="chip">
                      <template #option="slotProps">
                        <div class="flex items-center">
                          <Tag
                            :style="{ background: slotProps.option.color, color: getTextColor(slotProps.option.color) }">
                            <span class="text-xs font-medium">
                              {{ slotProps.option.name }}
                            </span>
                          </Tag>
                        </div>
                      </template>
                    </MultiSelect>
                  </div>
                  <div class="flex flex-col gap-2 w-2/3">
                    <label for="note">{{ t('shortlink.administration.comment') }}</label>
                    <InputText v-model="formData.note" :disabled="!user.permissions.create_shortlinks_with_a_note" />
                  </div>
                </div>
              </div>
            </div>

            <div v-if="user.permissions.create_shortlinks_with_12_month_expiration && !formData.shortlinkLoading"
              class="flex flex-col">
              <div class="flex items-center space-x-4">
                <Fieldset pt:legend:class="!text-xs" legend="RGPD">
                  <div class="flex space-x-4">
                    <Checkbox v-model="formData.gdpr12month" binary />
                    <label for="gdpr12month">J’atteste que l’url de destination ne contient pas de données personnelles.
                      Le
                      lien court peut donc être conservé au-delà d’une durée de 12 mois.</label>
                  </div>
                </Fieldset>
              </div>
            </div>

            <div class="flex flex-col mt-1"
              v-if="!formData.shortlinkLoading && !user.permissions.update_shortlink && !formData.uuid">
              <Message icon="pi pi-info-circle" severity="info">{{ t('shortlink.premium') }}
              </Message>
            </div>
            <div class="flex flex-col mt-1" v-if="!isAllowed && !formData.uuid">
              <Message icon="pi pi-info-circle" severity="info">{{ t('shortlink.notallowed') }}
              </Message>
            </div>
          </form>
        </div>
      </div>
      <template #footer>
        <div v-if="!formData.shortlinkLoading" class="flex items-center gap-2">
          <Button @click="handleHide" :label="t('common.labels.cancel')" class="flex-auto" outlined />
          <Button v-if="!formData.uuid" type="submit" @click="onSubmit" :loading="formData.generateLoading"
            :disabled="!isAllowed || !errors" :label="t('common.labels.create')" />
          <Button v-else type="submit" @click="onSubmit" :loading="formData.generateLoading"
            :label="t('common.labels.save')" :disabled="!user.permissions.update_shortlink && Boolean(formData.uuid)" />
        </div>
      </template>
    </Drawer>
  </Fluid>
</template>
